import type { PdfiumApi, PdfiumTypeMap } from "@pdfking/pdfium-wrap"
import type {
  PDFRasterizeFunction,
  PDFRasterizeResult,
  PDFReaderOptions,
  Pixel,
} from "@pdfking/renderer-api"
import type { Bytes } from "@pdfking/tfunc"
import type { ReadDocumentResult } from "../functions/ReadDocument"
import { ExportImage } from "@pdfking/compatibility"
import { PdfiumExports as E } from "@pdfking/pdfium-wrap"
import { CloseDocument } from "../functions/CloseDocument"
import { EnsurePage } from "../functions/EnsurePage"
import { ReadDocument } from "../functions/ReadDocument"
import { PageRange } from "../utils/PageRange"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerBitmap = PdfiumTypeMap["FPDF_BITMAP"]
type PointerPage = PdfiumTypeMap["FPDF_PAGE"]

const renderPage = async (
  api: PdfiumApi,
  reader: ReadDocumentResult,
  page: number,
  image_resolution: Pixel = 72 as Pixel
): Promise<PDFRasterizeResult> => {
  await EnsurePage(api, reader, page)
  const { doc_pointer } = reader
  const { call, readBytes, safeCall } = api
  let page_pointer: null | PointerPage = null
  let bitmap_pointer: null | PointerBitmap = null

  try {
    page_pointer = await safeCall(E.FPDF_LoadPage, doc_pointer, page - 1)

    // PDF points to pixels: points * (resolution / 72)
    const width = await call(E.FPDF_GetPageWidthF, page_pointer)
    const height = await call(E.FPDF_GetPageHeightF, page_pointer)
    const scale = image_resolution / 72

    const scaled_width = Math.floor(width * scale)
    const scaled_height = Math.floor(height * scale)

    bitmap_pointer = await safeCall(
      E.FPDFBitmap_Create,
      scaled_width,
      scaled_height,
      1
    )

    await call(
      E.FPDF_RenderPageBitmap,
      bitmap_pointer,
      page_pointer,
      0,
      0,
      scaled_width,
      scaled_height,
      0,
      16 // FPDF_REVERSE_BYTE_ORDER
    )

    const bufferPtr = await safeCall(E.FPDFBitmap_GetBuffer, bitmap_pointer)

    const data = readBytes(
      bufferPtr,
      (scaled_width * scaled_height * 4) as Bytes
    )

    return {
      blob: await ExportImage({
        data,
        height: scaled_height as Pixel,
        width: scaled_width as Pixel,
      }),
      size: {
        height: scaled_height as Pixel,
        width: scaled_width as Pixel,
      },
    }
  } finally {
    if (bitmap_pointer) await call(E.FPDFBitmap_Destroy, bitmap_pointer)
    if (page_pointer) await call(E.FPDF_ClosePage, page_pointer)
  }
}

async function* PDFRasterizeImpl(
  api: PdfiumApi,
  input: PDFReaderOptions,
  image_resolution: Pixel,
  rangeFn?: (page_count: number) => Iterable<number>
): AsyncGenerator<PDFRasterizeResult> {
  const reader = await ReadDocument(api, input)

  try {
    const page_count = await api.call(E.FPDF_GetPageCount, reader.doc_pointer)

    for (const page of PageRange(page_count, rangeFn)) {
      yield await renderPage(api, reader, page, image_resolution)
    }
  } finally {
    await CloseDocument(api, reader)
  }
}

export const PDFRasterize: PDFRasterizeFunction = (
  input,
  image_resolution = 72 as Pixel,
  rangeFn
) =>
  ProvisionPdfium(api =>
    PDFRasterizeImpl(api, input, image_resolution, rangeFn)
  )
